import { useState } from "react";
import { Logo } from "../components/Logo";
import { apiFetch } from "../services/http";
import { useAuth, type AuthUser } from "../auth/AuthContext";
import Icon from "../components/icons";
import { useLang, type Key } from "../hooks/useLang";

type View = "login" | "register";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const [view, setView] = useState<View>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const res = await apiFetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, scope: "customer" }),
      });
      if (!res.ok) {
        setErr(res.status === 401 ? t("login_bad_credentials") : `${t("login_failed")} (HTTP ${res.status}).`);
        return;
      }
      const data = (await res.json()) as { token: string; user: AuthUser };
      login(data.token, data.user);
    } catch {
      setErr(t("login_no_server"));
    } finally {
      setLoading(false);
    }
  }

  if (view === "register") {
    return (
      <div className="login-screen">
        <RegisterForm onBackToLogin={() => setView("login")} />
      </div>
    );
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="brand" style={{ justifyContent: "center", marginBottom: 8 }}>
          <Logo className="brand-logo" />
        </div>
        <h2 style={{ margin: "4px 0 16px", textAlign: "center" }}>{t("login_title")}</h2>
        <label>
          {t("login_email")}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </label>
        <label>
          {t("login_password")}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
        <button type="submit" disabled={loading || !email.trim() || !password} style={{ marginTop: 14 }}>
          {loading ? t("login_submitting") : t("login_submit")}
        </button>
        <div className="login-alt">
          {t("login_no_account")}{" "}
          <button type="button" className="login-link" onClick={() => setView("register")}>
            {t("login_go_register")}
          </button>
        </div>
      </form>
    </div>
  );
}

type AccountType = "individual" | "corporate";

// Parola politikası — backend auth.ValidatePasswordPolicy ile aynı kural:
// en az 10 kod noktası + büyük harf + küçük harf + rakam (Unicode duyarlı,
// Go'nun unicode.IsUpper/IsLower/IsDigit karşılığı).
function pwChecks(pw: string) {
  return {
    len: [...pw].length >= 10,
    upper: /\p{Lu}/u.test(pw),
    lower: /\p{Ll}/u.test(pw),
    digit: /\p{Nd}/u.test(pw),
  };
}
function pwValid(pw: string): boolean {
  const c = pwChecks(pw);
  return c.len && c.upper && c.lower && c.digit;
}

// generatePassword — politikayı kesin sağlayan 14 karakterlik güçlü parola.
// crypto.getRandomValues ile kriptografik rastgelelik; benzer görünen karakterler
// (0/O, 1/l/I) okunabilirlik için çıkarıldı.
function randInt(max: number): number {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0] % max;
}
function generatePassword(): string {
  const U = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const L = "abcdefghijkmnpqrstuvwxyz";
  const D = "23456789";
  const S = "!@#$%*?-_";
  const all = U + L + D + S;
  const chars = [U[randInt(U.length)], L[randInt(L.length)], D[randInt(D.length)], S[randInt(S.length)]];
  while (chars.length < 14) chars.push(all[randInt(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

// PasswordField — parola girişi + göster/gizle (göz) ve opsiyonel güçlü-parola
// öner (sparkles) ikonları. show/onToggleShow parent'tan kontrol edilir (öneri
// sonrası iki alanı da açığa çıkarabilmek için).
function PasswordField({ t, label, value, onChange, autoComplete, show, onToggleShow, onGenerate }: {
  t: (k: Key) => string;
  label: string; value: string; onChange: (v: string) => void; autoComplete: string;
  show: boolean; onToggleShow: () => void; onGenerate?: () => void;
}) {
  return (
    <label>
      {label}
      <div className="pw-field">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          required
        />
        {onGenerate && (
          <button type="button" className="pw-icon-btn" onClick={onGenerate} title={t("pw_suggest")} aria-label={t("pw_suggest")}>
            <Icon name="sparkles" size={16} />
          </button>
        )}
        <button type="button" className="pw-icon-btn" onClick={onToggleShow} title={show ? t("pw_hide") : t("pw_show")} aria-label={show ? t("pw_hide_aria") : t("pw_show_aria")}>
          <Icon name={show ? "eyeOff" : "eye"} size={16} />
        </button>
      </div>
    </label>
  );
}

// RegisterForm — bireysel + kurumsal self-servis kayıt. Backend:
// POST /api/v1/auth/register (org + kullanıcı oluşturur, JWT döner → auto-login).
function RegisterForm({ onBackToLogin }: { onBackToLogin: () => void }) {
  const { login } = useAuth();
  const { t } = useLang();
  const [type, setType] = useState<AccountType>("individual");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [identityNo, setIdentityNo] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const checks = pwChecks(password);

  function onGenerate() {
    const pw = generatePassword();
    setPassword(pw);
    setPassword2(pw);
    setShowPw(true);
    setShowPw2(true);
  }

  function validate(): string | null {
    if (type === "individual") {
      if (!fullName.trim()) return t("reg_req_full_name");
      if (!identityNo.trim()) return t("reg_req_identity_no");
    } else {
      if (!companyName.trim()) return t("reg_req_company_name");
      if (!taxId.trim()) return t("reg_req_tax_no");
      if (!fullName.trim()) return t("reg_req_contact_name");
    }
    if (!email.trim()) return t("reg_req_email");
    if (!pwValid(password)) return t("reg_weak_password");
    if (password !== password2) return t("reg_password_mismatch");
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) { setErr(v); return; }
    setErr(null);
    setLoading(true);
    try {
      const payload = {
        type: type === "individual" ? "bireysel" : "kurumsal",
        full_name: fullName.trim(),
        company_name: type === "corporate" ? companyName.trim() : "",
        identity_no: type === "individual" ? identityNo.trim() : "",
        tax_no: type === "corporate" ? taxId.trim() : "",
        tax_office: type === "corporate" ? taxOffice.trim() : "",
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        password,
      };
      const res = await apiFetch("/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (res.status === 409) { setErr(t("reg_email_taken")); return; }
        let msg = `${t("reg_failed")} (HTTP ${res.status}).`;
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch { /* yut */ }
        setErr(msg);
        return;
      }
      const data = (await res.json()) as { token: string; user: AuthUser };
      login(data.token, data.user); // auto-login → App müşteri panelini açar
    } catch {
      setErr(t("login_no_server"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="login-card" onSubmit={onSubmit}>
      <div className="brand" style={{ justifyContent: "center", marginBottom: 8 }}>
        <Logo className="brand-logo" />
      </div>
      <h2 style={{ margin: "4px 0 14px", textAlign: "center" }}>{t("reg_title")}</h2>

      <div className="reg-type-toggle">
        <button
          type="button"
          className={"opt" + (type === "individual" ? " opt-active" : "")}
          onClick={() => setType("individual")}
        >
          {t("reg_type_individual")}
        </button>
        <button
          type="button"
          className={"opt" + (type === "corporate" ? " opt-active" : "")}
          onClick={() => setType("corporate")}
        >
          {t("reg_type_corporate")}
        </button>
      </div>

      {type === "corporate" && (
        <>
          <label>
            {t("reg_company_name")}
            <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required autoFocus />
          </label>
          <label>
            {t("reg_tax_no")}
            <input value={taxId} onChange={(e) => setTaxId(e.target.value)} inputMode="numeric" required />
          </label>
          <label>
            {t("reg_tax_office")}
            <input value={taxOffice} onChange={(e) => setTaxOffice(e.target.value)} />
          </label>
        </>
      )}

      <label>
        {type === "corporate" ? t("reg_contact_name") : t("reg_full_name")}
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          required
          autoFocus={type === "individual"}
        />
      </label>

      {type === "individual" && (
        <label>
          {t("reg_identity_no")}
          <input value={identityNo} onChange={(e) => setIdentityNo(e.target.value)} inputMode="numeric" required />
        </label>
      )}

      <label>
        {t("login_email")}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
      </label>
      <label>
        {t("reg_phone")}
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
      </label>

      <PasswordField
        t={t}
        label={t("login_password")}
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        show={showPw}
        onToggleShow={() => setShowPw((s) => !s)}
        onGenerate={onGenerate}
      />
      {password.length > 0 && (
        <ul className="pw-policy" aria-label={t("pw_policy_aria")}>
          <li className={checks.len ? "ok" : ""}>{checks.len ? "✓" : "•"} {t("pw_rule_len")}</li>
          <li className={checks.upper ? "ok" : ""}>{checks.upper ? "✓" : "•"} {t("pw_rule_upper")}</li>
          <li className={checks.lower ? "ok" : ""}>{checks.lower ? "✓" : "•"} {t("pw_rule_lower")}</li>
          <li className={checks.digit ? "ok" : ""}>{checks.digit ? "✓" : "•"} {t("pw_rule_digit")}</li>
        </ul>
      )}

      <PasswordField
        t={t}
        label={t("reg_password_repeat")}
        value={password2}
        onChange={setPassword2}
        autoComplete="new-password"
        show={showPw2}
        onToggleShow={() => setShowPw2((s) => !s)}
      />

      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      <button type="submit" disabled={loading} style={{ marginTop: 14 }}>
        {loading ? t("reg_submitting") : t("reg_submit")}
      </button>
      <div className="login-alt">
        {t("reg_have_account")}{" "}
        <button type="button" className="login-link" onClick={onBackToLogin}>
          {t("reg_go_login")}
        </button>
      </div>
    </form>
  );
}
